import { Component } from '@theme/component';

/**
 * Displays reading progress for the advertorial article.
 *
 * @typedef {object} AdvertorialReadingProgressRefs
 * @property {HTMLElement} fill
 * @extends Component<AdvertorialReadingProgressRefs>
 */
class AdvertorialReadingProgress extends Component {
  requiredRefs = ['fill'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #animationFrame;

  /** @type {HTMLElement | undefined} */
  #pageWrapper;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const pageWrapper = this.closest('.page-wrapper');
    this.#pageWrapper = pageWrapper instanceof HTMLElement ? pageWrapper : undefined;

    const options = { passive: true, signal: this.#abortController.signal };
    this.#pageWrapper?.addEventListener('scroll', this.#scheduleUpdate, options);
    window.addEventListener('scroll', this.#scheduleUpdate, options);
    window.addEventListener('resize', this.#scheduleUpdate, options);

    const article = this.parentElement?.querySelector('.acacia-advertorial__article');
    if (article) {
      this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
      this.#resizeObserver.observe(article);
    }

    this.#scheduleUpdate();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();

    if (this.#animationFrame !== undefined) {
      window.cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = undefined;
    }
  }

  #scheduleUpdate = () => {
    if (this.#animationFrame !== undefined) return;

    this.#animationFrame = window.requestAnimationFrame(() => {
      this.#animationFrame = undefined;
      this.#updateProgress();
    });
  };

  #updateProgress() {
    const article = this.parentElement?.querySelector('.acacia-advertorial__article');
    if (!(article instanceof HTMLElement)) return;

    const wrapperOverflow = this.#pageWrapper ? getComputedStyle(this.#pageWrapper).overflowY : '';
    const usesPageWrapper =
      this.#pageWrapper &&
      this.#pageWrapper.scrollHeight > this.#pageWrapper.clientHeight &&
      (wrapperOverflow === 'auto' || wrapperOverflow === 'scroll');
    const scrollTop = usesPageWrapper
      ? this.#pageWrapper.scrollTop
      : (document.scrollingElement?.scrollTop ?? window.scrollY);
    const viewportHeight = usesPageWrapper ? this.#pageWrapper.clientHeight : window.innerHeight;
    const scrollerTop = usesPageWrapper ? this.#pageWrapper.getBoundingClientRect().top : 0;
    const articleTop = article.getBoundingClientRect().top + scrollTop - scrollerTop;
    const readableDistance = Math.max(1, article.offsetHeight - viewportHeight);
    const progress = Math.min(1, Math.max(0, (scrollTop - articleTop) / readableDistance));

    this.style.setProperty('--advertorial-reading-progress', progress.toFixed(4));
  }
}

if (!customElements.get('advertorial-reading-progress')) {
  customElements.define('advertorial-reading-progress', AdvertorialReadingProgress);
}
